import { Component, ViewChild, ViewEncapsulation } from "@angular/core";
import { User } from "@task-modules/users";
import { TaskRoutes } from "@task-utils/routes";
import { Task } from "@task-utils/types";
import { DataTableComponent, Frame, GenericFilterComponent, language, modal, TableConfig, template, GenericFilter, utils } from "curvy";
import { jobs_management_translations } from "./jobs.trans";
import { TaskFilterComponent } from "@task-components/single-row-filter/single-row-filter.component";
import { CreateTempStaffRequestDialog } from "./create-temp-staff-request.dialog";
import { QrCodeDialog } from "./qrcode.dialog";


@Component({
   selector: 'task-temp-staff-requests',
   template: `
      <div class="temp-add-button"
         style="padding: 0 0.7em; gap: 0.5em;"
         *ngIf="can_create || can_start_arrival"
      >


         @if (can_start_arrival) {
            <button class="filled pill"
               color="primary"
               style="margin-left: auto;"
               (click)="show_qr_code('start')">
               <i icon="qr_code_scanner"></i>
               &nbsp;
               QR - {{ 'ARRIVAL_START' | trans }}
            </button>

            <button class="filled pill"
               color="primary"
               (click)="show_qr_code('end')">
               <i icon="qr_code_scanner"></i>
               &nbsp;
               QR - {{ 'ARRIVAL_END' | trans }}
            </button>
         }
         @if(can_create) {
            <button (click)="add_request()"
               class="filled pill"
               color="primary"
               style="margin-left: auto; align-self: center;"
            >
               <i icon="add_circle"></i>
               <span>{{ 'TEMP_REQUEST.ADD_NEW' | trans }}</span>
            </button>
         }
      </div>

      <knw-data-table #tempStaffRequestsTable [config]="tableConfig"></knw-data-table>

   `,
   encapsulation: ViewEncapsulation.None,
   styles: [`
      html task-temp-staff-requests {
         display: flex;
         flex-flow: column nowrap;
         overflow: hidden !important;

         .table-wrapper .table .table-cell.table-body-cell {
            display: flex;
            flex-flow: column nowrap;
         }

         .table-body-cell.table-cell.table-column-title {
            justify-content: flex-start;
            padding-left: 0.7em;
         }
      }

   `],
})

export class TempStaffRequestsComponent {
   @ViewChild('tempStaffRequestsTable') table: DataTableComponent<Task.TempStaffRequest, DisplayTempStaffRequest, 'temp_staff_requests', TaskFilterComponent>;
   can_create = false;
   can_start_arrival = false;

   constructor() {
      Frame.set({
         title: "NAVROUTE.TEMPS_REQUESTS",
         visible: true,
         layout: "middle",
         size: "full"
      });

      language.load_translations(jobs_management_translations);

      User.currentTypeIs(
         Task.USER_TYPE.STORE_MANAGER,
         Task.USER_TYPE.ADMIN,
         Task.USER_TYPE.SYSTEM_ADMIN
      ).then(res => {
         this.can_start_arrival = res;
         this.can_create = res;
      })

   }

   get_filters = async () => {
      let filters: TaskFilterComponent["filterInput"] = [
         GenericFilter.text("TITLE", "title"),
         GenericFilter.date_filter("TEMP_STAFF_REQUEST.DATE_FROM", "date_from"),
         GenericFilter.date_filter("TEMP_STAFF_REQUEST.DATE_TO", "date_to"),
         GenericFilter.search("LOCATION","location",  this.search_locations, this.format_location_name),

      ];

      return filters;
   }

   add_request() {
      modal.open(CreateTempStaffRequestDialog, res=>{
         if (res) {
            this.table.getData();
         }
      })
   }

   async search_locations(search: string) {
      let res = await TaskRoutes.locations.api_location_search_visible(
         { search: [search], attributes: ["cin+ +name"] },
         { page_no: 1, page_size: 50 }
      );
      return res.data;
   }

   format_location_name(loc: Task.Location) {
      if (!loc) { return ""; }
      return `${loc.cin ?? ''} ${loc.name}`;
   }

   show_qr_code(type: 'start' | 'end') {
      modal.open(QrCodeDialog, async (res)=>{
            console.log(res)
         },
         type
      )
   }

   tableConfig: TableConfig<Task.TempStaffRequest, DisplayTempStaffRequest, 'temp_staff_requests', GenericFilterComponent> = {
      tableName: 'temp_staff_requests',
      getData: async (filter, paginator, sort_header) => {
         let parsed_filter = { search: [], attributes: [], sort: null as TaskRoutes.Search_Params["sort"] };
         for (let key in filter) {
            if (key == 'location') {
               parsed_filter.attributes.push('location_id');
               parsed_filter.search.push(filter[key].location_id);
            } else {
               parsed_filter.attributes.push(key);
               parsed_filter.search.push(filter[key]);
            }
         }

         if (sort_header) {
            switch (sort_header.label) {
               case 'title':
                  sort_header.label = 'title';
                  break;

               case 'date':
                  sort_header.label = 'd:date_from';
                  break;

               case 'number_of_workers':
                  sort_header.label = '#:number_of_workers';
                  break;
            }

            parsed_filter.sort = sort_header;

         } else {
            parsed_filter.sort = [{
               attribute: "#:temp_staff_request_id",
               label: "#:temp_staff_request_id",
               direction: "desc",
               ascending: false
            }];
         }

         return await TaskRoutes.temp_staff_requests.api_temp_staff_requests_search(parsed_filter, paginator);
      },
      getHeader: (header) => {
         switch (header) {
            case 'status':
               return { icon: "offline_bolt", label: "", sortable: false };
            case 'title':
               return { icon: "title", label: "TITLE", sortable: true };
            case 'date':
               return { icon: "date_range", label: "DATE", sortable: true };
            case 'number_of_workers':
               return { icon: "people", label: "TEMP_STAFF_REQUEST.NUMBER_OF_WORKERS", sortable: true };
            case 'location':
               return { icon: "place", label: "LOCATION", sortable: false };
         }
      },

      unwrapData: (req) => {
         let date_temp = "";

         if (req.date_from == req.date_to) {
            date_temp = `
            <div style="white-space: nowrap">{{ date_from | date << short_date }}</div>`
         } else {
            date_temp = `<div style="white-space: nowrap">{{ date_from | date << short_date }} - {{ date_to | date << short_date }}</div>`
         }

         let status_name = "x";
         let status_icon = "x";
         let status_color = "x";

         let applications_over = new Date() >= new Date(req.date_from);
         let work_over = new Date() > new Date(req.date_to);
         let candidates_selected = req.accepted_count < req.number_of_workers;

         if (!applications_over) {
            if (candidates_selected) {
               status_name = "TEMP_STAFF_REQUEST.STATUS.APPLICATIONS_OPEN";
               status_icon = "assignment";
               status_color = Task.STATUS_COLORS.YELLOW;

            } else {
               status_name = "TEMP_STAFF_REQUEST.STATUS.APPLICATIONS_FINISHED";
               status_icon = "assignment_turned_in";
               status_color = Task.STATUS_COLORS.BRIGHT_YELLOW;
            }

         } else if (!work_over) {
            status_name = "TEMP_STAFF_REQUEST.STATUS.WORKS_UNDERWAY";
            status_icon = "event";
            status_color = Task.STATUS_COLORS.BLUE;

         } else {
            status_name = "TEMP_STAFF_REQUEST.STATUS.WORKS_FINISHED";
            status_icon = "check_circle";
            status_color = Task.STATUS_COLORS.ORANGE;
         }

         return {
            status: template.transform(`
               <div class="flex-row align-center justify-center">
                  <i style="color: ${status_color}" icon="${status_icon}"></i>
               </div>
               <div style="font-size: 0.8em">
                  {{ status_name | translate }}
               </div>
            `, {
              status_icon, status_name
            }),

            title: template.transform(`
               <span class="flex-row" style="color: rgb(var(--primary)); gap: 1ch;">
                  <a href="/temp-staff-requests/{{id}}">
                     #{{id}}
                  </a>
                  <span class="truncate-text">{{ title }}</span>
               </span>
            `, {
               title: req.title,
               id: req.temp_staff_request_id
             }),
            date: template.transform(date_temp, {
               date_from: req.date_from,
               date_to: req.date_to
             }),
            number_of_workers: template.transform(`
               <div style="white-space: nowrap">{{ number_of_workers }}</div>
            `, { number_of_workers: req.number_of_workers }),
            location: template.transform(`
               <div style="white-space: nowrap">{{ location }}</div>
            `, { location: req.location_name }),
         }
      },
      maxActions: window.innerWidth < 600 ? 1 : 3,
      filterComponent: GenericFilterComponent,
      filterInput: this.get_filters,
      rowActions: [
         {
            label: language.translate('EDIT'),
            icon: "edit",
            onClick: (temp_staff_request) => {
              utils.router.navigateByUrl("/temp-staff-requests/" + temp_staff_request.temp_staff_request_id);
            },
            priority: 1,
            isVisible: () => true,
         },
      ]
   }

}

type DisplayTempStaffRequest = {
   status: template.EscapedHTML,
   title: template.EscapedHTML,
   date: template.EscapedHTML,
   number_of_workers: template.EscapedHTML,
   location: template.EscapedHTML,
}
